import { lazy, Suspense, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';

// The borrower and collector portals (docs §237) load only when such a login opens them.
const ChannelLayout = lazy(() => import('./ChannelLayout'));
const BorrowerHomePage = lazy(() => import('./BorrowerPages').then((m) => ({ default: m.BorrowerHomePage })));
const BorrowerLoanPage = lazy(() => import('./BorrowerPages').then((m) => ({ default: m.BorrowerLoanPage })));
const BorrowerApplyPage = lazy(() => import('./BorrowerPages').then((m) => ({ default: m.BorrowerApplyPage })));
const BorrowerProofOfLifePage = lazy(() => import('./BorrowerPages').then((m) => ({ default: m.BorrowerProofOfLifePage })));
const BorrowerHelpPage = lazy(() => import('./BorrowerPages').then((m) => ({ default: m.BorrowerHelpPage })));
const BorrowerSettingsPage = lazy(() => import('./BorrowerPages').then((m) => ({ default: m.BorrowerSettingsPage })));
const CollectorCasesPage = lazy(() => import('./CollectorPages').then((m) => ({ default: m.CollectorCasesPage })));
const CollectorStatementPage = lazy(() => import('./CollectorPages').then((m) => ({ default: m.CollectorStatementPage })));

const wait = (element: ReactNode) => (
  <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}>{element}</Suspense>
);

const frame = (kind: 'borrower' | 'collector', permission: string, children: RouteObject[]): RouteObject => ({
  path: kind,
  element: wait(<RequirePermission permission={permission}><RequireModule module={Modules.Lending}><ChannelLayout kind={kind} /></RequireModule></RequirePermission>),
  children,
});

export const lendingChannelRoutes: RouteObject[] = [
  frame('borrower', Perm.Lending.Borrower, [
    { index: true, element: wait(<BorrowerHomePage />) },
    { path: 'loan/:id', element: wait(<BorrowerLoanPage />) },
    { path: 'apply', element: wait(<BorrowerApplyPage />) },
    { path: 'proof-of-life', element: wait(<BorrowerProofOfLifePage />) },
    { path: 'help', element: wait(<BorrowerHelpPage />) },
    { path: 'settings', element: wait(<BorrowerSettingsPage />) },
  ]),
  frame('collector', Perm.Lending.Collector, [
    { index: true, element: wait(<CollectorCasesPage />) },
    { path: 'statement', element: wait(<CollectorStatementPage />) },
  ]),
];
